import { presentation } from '../features/agency/service';
import { SourceFilters, Provenance } from '../features/agency/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { isApiError } from '@aiptyg/types';
import {
  useTaskList,
  changeTaskStatus,
  createTask,
  deleteTask,
  TASK_STATUS_LABELS,
  TASK_STATUS_ACTION_LABELS,
  TASK_CATEGORY_LABELS,
  TASK_TYPE_LABELS,
  TASK_PLATFORM_LABELS,
  ALL_TASK_STATUSES,
  ALL_TASK_CATEGORIES,
  ALL_TASK_TYPES,
  ALL_TASK_PLATFORMS,
} from '../features/tasks';
import type { CreateTaskRequest, TaskDTO, TaskStatus, TaskType, TaskPlatform } from '../features/tasks';
import { ConfirmDialog } from '../components/feedback/ConfirmDialog';
import { useToastStore } from '../store/toast.store';
import { normalizeImageValueForSubmit } from '../lib/upload/cos';
import { useMarketConfigs } from '../features/settings';

const STATUS_STYLES: Record<TaskStatus, string> = {
  draft: 'bg-gray-100 text-gray-600',
  active: 'bg-blue-50 text-blue-700',
  paused: 'bg-amber-50 text-amber-700',
  archived: 'bg-gray-100 text-gray-500',
};

const TYPE_STYLES: Record<TaskType, string> = {
  normal: 'bg-gray-100 text-gray-600',
  other_normal: 'bg-sky-100 text-sky-700',
  download: 'bg-purple-50 text-purple-700',
};

const BULK_STATUS_TARGETS: TaskStatus[] = ['active', 'paused', 'archived'];

interface DeleteTarget {
  ids: number[];
  count: number;
  title?: string;
}

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function canTransitionTaskStatus(current: string, next: TaskStatus): boolean {
  if (current === next) return true;
  switch (current) {
    case 'draft':
      return next === 'active' || next === 'archived';
    case 'active':
      return next === 'paused' || next === 'archived';
    case 'paused':
      return next === 'active' || next === 'archived';
    case 'archived':
      return next === 'paused' || next === 'active';
    default:
      return false;
  }
}

function resolveActionError(error: unknown): string {
  if (isApiError(error)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      task_not_found: '任务不存在',
      invalid_task_status: '任务状态不合法',
      invalid_status_transition: '状态变更不合法',
      invalid_request_body: '请求参数不合法',
      network_error: '网络连接失败',
    };
    return map[error.message] ?? `操作失败 (${error.message})`;
  }
  return '操作失败';
}

function buildTaskCreateRequest(task: TaskDTO): CreateTaskRequest {
  return {
    title: task.title,
    description: task.description,
    description_title: task.description_title || '',
    category: task.category,
    task_type: task.task_type,
    platform: task.platform,
    reward_amount_fen: task.reward_amount_fen,
    market_id: task.market_id,
    country_code: task.country_code,
    currency: task.currency,
    currency_symbol: task.currency_symbol,
    currency_exponent: task.currency_exponent,
    reward_amount_minor: task.reward_amount_minor,
    completed_count: task.completed_count,
    max_assignments: task.max_assignments,
    assignment_timeout_seconds: task.assignment_timeout_seconds ?? 120,
    require_review: task.require_review,
    is_recommended: task.is_recommended,
    is_new: task.is_new,
    icon_url: normalizeImageValueForSubmit(task.icon_url || ''),
    tags: task.tags ?? [],
    rules: task.rules || '',
    rules_title: task.rules_title || '',
    review_rules: task.review_rules || '',
    review_rules_title: task.review_rules_title || '',
    steps: (task.steps ?? []).map((step) => ({
      ...step,
      images: (step.images ?? []).map((image) => normalizeImageValueForSubmit(image)),
    })),
    app_info: (task.task_type === 'download' || task.task_type === 'normal') ? task.app_info : null,
    sort_order: task.sort_order ?? 0,
    start_at: task.start_at ?? undefined,
    end_at: task.end_at ?? undefined,
  };
}

export function TasksPage() {
  const {
    items, total, page, pageSize, totalPages,
    status, marketId, category, taskType, platform,
    loading, error,
    setPage, setPageSize, setStatus, setMarketId, setCategory, setTaskType, setPlatform, reload,
  } = useTaskList();
  const { items: marketConfigs } = useMarketConfigs();
  const addToast = useToastStore((s) => s.addToast);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkUpdating, setBulkUpdating] = useState<TaskStatus | null>(null);
  const [sortDrafts, setSortDrafts] = useState<Record<number, string>>({});
  const [sortSavingId, setSortSavingId] = useState<number | null>(null);
  const [copyingId, setCopyingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleting, setDeleting] = useState(false);

  const currentPageIds = useMemo(() => items.filter(item => !item.agent_portal_task_id).map((item) => item.id), [items]);
  const selectedTasks = useMemo(
    () => items.filter((item) => selectedIds.includes(item.id)),
    [items, selectedIds],
  );
  const allCurrentPageSelected = currentPageIds.length > 0 && currentPageIds.every((id) => selectedIds.includes(id));

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => currentPageIds.includes(id)));
  }, [currentPageIds]);

  useEffect(() => {
    setSortDrafts(Object.fromEntries(items.map((item) => [item.id, String(item.sort_order ?? 0)])));
  }, [items]);

  const toggleSelected = useCallback((taskId: number) => {
    setSelectedIds((prev) => (
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    ));
  }, []);

  const toggleCurrentPage = useCallback(() => {
    setSelectedIds((prev) => {
      if (allCurrentPageSelected) {
        return prev.filter((id) => !currentPageIds.includes(id));
      }
      const next = new Set(prev);
      currentPageIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  }, [allCurrentPageSelected, currentPageIds]);

  const handleBulkStatus = useCallback(async (target: TaskStatus) => {
    if (selectedTasks.length === 0 || bulkUpdating) return;
    const invalid = selectedTasks.find((task) => task.agent_portal_task_id || !canTransitionTaskStatus(task.status, target));
    if (invalid) {
      addToast('error', `任务 #${invalid.id} 当前状态不能${TASK_STATUS_ACTION_LABELS[target]}`);
      return;
    }

    setBulkUpdating(target);
    try {
      await Promise.all(selectedTasks.map((task) => changeTaskStatus(task.id, target)));
      addToast('success', `已${TASK_STATUS_ACTION_LABELS[target]} ${selectedTasks.length} 个任务`);
      setSelectedIds([]);
      reload();
    } catch (e) {
      addToast('error', resolveActionError(e));
    } finally {
      setBulkUpdating(null);
    }
  }, [addToast, bulkUpdating, reload, selectedTasks]);

  const handleCopyTask = useCallback(async (task: TaskDTO) => {
    if (copyingId != null || task.agent_portal_task_id) return;

    setCopyingId(task.id);
    try {
      await createTask(buildTaskCreateRequest(task));
      addToast('success', '任务已复制为未发布草稿');
      reload();
    } catch (e) {
      addToast('error', resolveActionError(e));
    } finally {
      setCopyingId(null);
    }
  }, [addToast, copyingId, reload]);

  const openBulkDeleteConfirm = useCallback(() => {
    if (selectedTasks.length === 0 || deleting) return;
    if (selectedTasks.some(task => task.agent_portal_task_id)) { addToast('error', '商户任务请在全部商户任务中控制'); return; }
    setDeleteTarget({
      ids: selectedTasks.map((task) => task.id),
      count: selectedTasks.length,
    });
  }, [deleting, selectedTasks]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget || deleting) return;

    setDeleting(true);
    try {
      await Promise.all(deleteTarget.ids.map((id) => deleteTask(id)));
      addToast('success', `已删除 ${deleteTarget.count} 个任务`);
      setSelectedIds([]);
      setDeleteTarget(null);
      reload();
    } catch (e) {
      addToast('error', resolveActionError(e));
    } finally {
      setDeleting(false);
    }
  }, [addToast, deleteTarget, deleting, reload]);

  const handleSaveSortOrder = useCallback(async (task: TaskDTO) => {
    const nextSortOrder = Number.parseInt(sortDrafts[task.id] ?? String(task.sort_order ?? 0), 10);
    if (!Number.isFinite(nextSortOrder)) {
      addToast('error', '排序值必须是整数');
      return;
    }

    setSortSavingId(task.id);
    try {
      await presentation(task.id, nextSortOrder, task.is_recommended);
      addToast('success', '排序已保存');
      reload();
    } catch (e) {
      addToast('error', resolveActionError(e));
    } finally {
      setSortSavingId(null);
    }
  }, [addToast, reload, sortDrafts]);

  return (
    <div className="space-y-4">
      <SourceFilters />
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">管理所有任务，支持按状态、分类、类型和平台筛选。</p>
        <Link
          to="/tasks/create"
          className="px-4 py-2 text-sm rounded-md bg-primary text-white hover:bg-primary/90 transition-colors shrink-0"
        >
          创建任务
        </Link>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">全部状态</option>
          {ALL_TASK_STATUSES.map((s) => (
            <option key={s} value={s}>{TASK_STATUS_LABELS[s]}</option>
          ))}
        </select>

        <select
          value={marketId}
          onChange={(e) => setMarketId(e.target.value)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">全部市场</option>
          {marketConfigs.map((market) => (
            <option key={market.market_id} value={market.market_id}>{market.market_id} · {market.name}</option>
          ))}
        </select>

        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">全部分类</option>
          {ALL_TASK_CATEGORIES.map((c) => (
            <option key={c} value={c}>{TASK_CATEGORY_LABELS[c]}</option>
          ))}
        </select>

        <select
          value={taskType}
          onChange={(e) => setTaskType(e.target.value)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">全部类型</option>
          {ALL_TASK_TYPES.map((t) => (
            <option key={t} value={t}>{TASK_TYPE_LABELS[t]}</option>
          ))}
        </select>

        <select
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">全部平台</option>
          {ALL_TASK_PLATFORMS.map((p) => (
            <option key={p} value={p}>{TASK_PLATFORM_LABELS[p]}</option>
          ))}
        </select>

        <button
          onClick={reload}
          className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          刷新
        </button>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm">
        <span className="text-gray-600">已选择 {selectedTasks.length} 个任务</span>
        <div className="flex items-center gap-2">
          {BULK_STATUS_TARGETS.map((target) => (
            <button
              key={target}
              onClick={() => {
                void handleBulkStatus(target);
              }}
              disabled={selectedTasks.length === 0 || bulkUpdating != null}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40"
            >
              {bulkUpdating === target ? '处理中...' : `批量${TASK_STATUS_ACTION_LABELS[target]}`}
            </button>
          ))}
          <button
            onClick={openBulkDeleteConfirm}
            disabled={selectedTasks.length === 0 || deleting}
            className="rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-600 transition-colors hover:bg-red-50 disabled:opacity-40"
          >
            {deleting && deleteTarget?.count !== 1 ? '删除中...' : '批量删除'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="py-10 text-center">
          <p className="text-sm text-red-500 mb-3">{error}</p>
          <button onClick={reload} className="text-sm text-primary hover:underline">重试</button>
        </div>
      )}

      {/* Loading */}
      {loading && !error && (
        <div className="flex items-center justify-center py-20">
          <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <>
          {items.length === 0 ? (
            <div className="py-20 text-center text-sm text-gray-400">暂无数据</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="w-10 px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={allCurrentPageSelected}
                        disabled={currentPageIds.length === 0 || bulkUpdating != null}
                        onChange={toggleCurrentPage}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50 disabled:opacity-40"
                      />
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">任务名</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">市场</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">类型</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">平台</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">标签</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">奖励</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">前端排序</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">完成数</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">名额</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">更新时间</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((task) => (
                    <tr key={task.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 align-top">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(task.id)}
                          disabled={bulkUpdating != null || Boolean(task.agent_portal_task_id)}
                          onChange={() => toggleSelected(task.id)}
                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50 disabled:opacity-40"
                        />
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900 max-w-[200px] truncate" title={task.title}>
                        {task.title}<Provenance item={task}/>
                      </td>
                      <td className="px-4 py-3 text-center text-xs text-gray-600">
                        {task.market_id || 'CN'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_STYLES[task.task_type as TaskType] ?? 'bg-gray-100 text-gray-600'}`}>
                          {TASK_TYPE_LABELS[task.task_type as TaskType] ?? task.task_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600 text-xs">
                        {TASK_PLATFORM_LABELS[task.platform as TaskPlatform] ?? task.platform}
                      </td>
                      <td className="px-4 py-3 text-gray-500 max-w-[140px] truncate text-xs" title={(task.tags ?? []).join(', ')}>
                        {(task.tags ?? []).length > 0 ? (task.tags ?? []).join(', ') : '-'}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                        {task.reward_display || `¥${task.reward_amount_yuan}`}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="number"
                            value={sortDrafts[task.id] ?? String(task.sort_order ?? 0)}
                            onChange={(event) => setSortDrafts((prev) => ({ ...prev, [task.id]: event.target.value }))}
                            className="h-8 w-20 rounded-md border border-gray-300 px-2 text-center text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
                          />
                          <button
                            onClick={() => {
                              void handleSaveSortOrder(task);
                            }}
                            disabled={sortSavingId === task.id}
                            className="rounded-md px-2 py-1 text-xs text-primary hover:bg-primary/10 disabled:opacity-40"
                          >
                            {sortSavingId === task.id ? '保存中' : '保存'}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-600">
                        {task.completed_count}
                      </td>
                      <td className="px-4 py-3 text-center tabular-nums text-gray-600">
                        {task.current_assigned}/{task.max_assignments === 0 ? '∞' : task.max_assignments}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                            STATUS_STYLES[task.status as TaskStatus] ?? 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {TASK_STATUS_LABELS[task.status as TaskStatus] ?? task.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500 whitespace-nowrap text-xs">
                        {formatDate(task.updated_at)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-3 whitespace-nowrap">
                        <Link
                          to={`/tasks/${task.id}`}
                          className="text-sm text-primary hover:underline"
                        >
                          编辑
                        </Link>
                          <button
                            onClick={() => {
                              void handleCopyTask(task);
                            }}
                            disabled={copyingId != null || deleting || Boolean(task.agent_portal_task_id)}
                            className="text-sm text-gray-600 hover:text-primary disabled:opacity-40"
                          >
                            {copyingId === task.id ? '复制中' : '复制'}
                          </button>
                          <button
                            onClick={() => setDeleteTarget({
                              ids: [task.id],
                              count: 1,
                              title: task.title,
                            })}
                            disabled={deleting || Boolean(task.agent_portal_task_id)}
                            className="text-sm text-red-600 hover:text-red-700 disabled:opacity-40"
                          >
                            删除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between pt-2 text-sm text-gray-500">
            <span>共 {total} 条</span>
            <div className="flex items-center gap-2">
              <select
                value={pageSize}
                onChange={(event) => setPageSize(Number(event.target.value))}
                className="h-9 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {[50, 100].map((value) => (
                  <option key={value} value={value}>每页 {value} 条</option>
                ))}
              </select>
              <button
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                上一页
              </button>
              <span className="text-sm tabular-nums">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                下一页
              </button>
            </div>
          </div>
        </>
      )}
      <ConfirmDialog
        open={deleteTarget !== null}
        title={deleteTarget?.count === 1 ? '删除任务' : '批量删除任务'}
        description={
          deleteTarget?.count === 1
            ? `确认删除任务「${deleteTarget.title ?? ''}」？已有领取记录的任务会从列表移除，并保留历史记录。`
            : `确认删除选中的 ${deleteTarget?.count ?? 0} 个任务？已有领取记录的任务会从列表移除，并保留历史记录。`
        }
        confirmLabel="删除"
        variant="danger"
        loading={deleting}
        onConfirm={() => {
          void handleConfirmDelete();
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
